// src/pages/dashboard/ReviewsDashboard.js

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataContext } from '../../contexts/DataContext';
import dataService from '../../services/dataService';
import { useGoogleOAuthStatus } from '../../hooks/useGoogleOAuthStatus';
import { getEffectiveUserId } from '../../services/accountScopedDataService';
import { getCurrentUser } from '../../services/authorizationService';
import { 
  ArrowRight, 
  Funnel, 
  PencilSimple
} from '@phosphor-icons/react';
import NoReviewsView from './NoReviewsView';

export default function ReviewsDashboard() {
  const navigate = useNavigate();
  const { dataCache, setDataCache, setIsLoading } = useDataContext();
  const effectiveUserId = getEffectiveUserId();
  const currentUser = getCurrentUser();
  const adminUserId = currentUser?.uid;
  const firebaseAccountId = currentUser?.accountId || effectiveUserId;
  const { isConnected: isGoogleConnected, isLoading: isOAuthLoading, startOAuth } = useGoogleOAuthStatus();

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    sortBy: 'newest',
    unrepliedOnly: false,
    needsAttention: false,
    starRating: null,
  });

  const gbpReviews = dataCache.gbpReviews || null;
  const reviews = useMemo(() => gbpReviews?.reviews || [], [gbpReviews]);
  const ratingSummary = gbpReviews?.summary || null;

  const hasRealData = Array.isArray(reviews) && reviews.length > 0;
  const hasLocationMappings = (gbpReviews?.locationsIncluded?.length || 0) > 0;

  const filterOptions = [
    { id: 'newest', label: 'Newest First' },
    { id: 'oldest', label: 'Oldest First' },
    { id: 'unreplied', label: 'Unreplied Only' },
    { id: 'needsAttention', label: 'Needs Attention' },
    { id: '5star', label: '5 Star Reviews' },
    { id: '4star', label: '4 Star Reviews' },
    { id: '3star', label: '3 Star Reviews' },
    { id: '2star', label: '2 Star Reviews' },
    { id: '1star', label: '1 Star Reviews' },
  ];

  useEffect(() => {
    const load = async () => {
      if (!adminUserId || !firebaseAccountId) return;
      if (!isGoogleConnected) return;

      setIsLoading(true);
      try {
        const data = await dataService.refreshData(adminUserId, 'gbpReviews', firebaseAccountId);
        setDataCache((prev) => ({
          ...prev,
          gbpReviews: data,
          lastUpdated: new Date().toISOString(),
        }));
      } catch (e) {
        // Leave existing state; UI will show empty/connection messaging
        console.error('Failed to load GBP reviews:', e);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [adminUserId, firebaseAccountId, isGoogleConnected, setDataCache, setIsLoading]);

  const handleFilterChange = (filterId) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      if (filterId === 'newest' || filterId === 'oldest') {
        newFilters.sortBy = filterId;
      } else if (filterId === 'unreplied') {
        newFilters.unrepliedOnly = !prev.unrepliedOnly;
      } else if (filterId === 'needsAttention') {
        newFilters.needsAttention = !prev.needsAttention;
      } else if (filterId.endsWith('star')) {
        const rating = parseInt(filterId.charAt(0));
        newFilters.starRating = newFilters.starRating === rating ? null : rating;
      }
      
      return newFilters;
    });
  };

  const filteredReviews = useMemo(() => {
    let filtered = [...reviews];

    if (activeFilters.unrepliedOnly) {
      filtered = filtered.filter(review => !review.reply);
    }
    if (activeFilters.needsAttention) {
      filtered = filtered.filter(review => (typeof review.rating === 'number' ? review.rating <= 3 : false));
    }
    if (activeFilters.starRating) {
      filtered = filtered.filter(review => review.rating === activeFilters.starRating);
    }

    const getTime = (r) => {
      const t = r.updateTime || r.createTime;
      const ms = t ? new Date(t).getTime() : 0;
      return Number.isFinite(ms) ? ms : 0;
    };
    if (activeFilters.sortBy === 'oldest') {
      filtered.sort((a, b) => getTime(a) - getTime(b));
    } else {
      filtered.sort((a, b) => getTime(b) - getTime(a));
    }
    return filtered;
  }, [reviews, activeFilters]);

  if (isOAuthLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Checking Google connection…</span>
      </div>
    );
  }

  if (!isGoogleConnected) {
    return <NoReviewsView onConnect={startOAuth} />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Feedback & Trust Hub</h2>
          <p className="text-gray-600">Manage customer reviews and build trust with your audience</p>
          
          {/* Data Source Indicator */}
          {hasRealData ? (
            <div className="mt-2 inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Connected to Google Business Profile</span>
            </div>
          ) : (
            <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>
                {hasLocationMappings
                  ? 'No reviews found for linked locations yet'
                  : 'Connected — link locations in Account Settings to pull reviews'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex gap-8">
        {/* Reviews List */}
        <div className="flex-1 space-y-6">
          {!hasLocationMappings ? (
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <div className="text-gray-800 font-semibold mb-2">Link your locations</div>
              <p className="text-sm text-gray-600 mb-4">
                You’re connected to Google, but no Shift locations are linked to Google Business Profile locations yet.
                Go to Account Settings → Google Services and map each location.
              </p>
              <button
                onClick={() => navigate('/dashboard/settings')}
                className="bg-[#000c34] hover:bg-[#000c34]/90 text-white text-sm px-4 py-2 rounded"
              >
                Open Account Settings
              </button>
            </div>
          ) : null}

          <div className="flex justify-start relative">
            <button 
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="text-sm text-blue-600 flex items-center gap-2 hover:text-blue-800 transition-colors"
            >
              <Funnel className="text-blue-600" weight="regular" />
              Filter by
            </button>

            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4 space-y-3">
                  <div className="text-sm font-semibold text-gray-800 mb-3">Sort & Filter</div>
                  
                  {/* Sort Options */}
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Sort</div>
                    {filterOptions.slice(0, 2).map(option => (
                      <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="sort"
                          checked={activeFilters.sortBy === option.id}
                          onChange={() => handleFilterChange(option.id)}
                          className="text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Filter Options */}
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Filters</div>
                    {filterOptions.slice(2, 4).map(option => (
                      <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            (option.id === 'unreplied' && activeFilters.unrepliedOnly) ||
                            (option.id === 'needsAttention' && activeFilters.needsAttention)
                          }
                          onChange={() => handleFilterChange(option.id)}
                          className="text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Star Rating Filters */}
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Star Rating</div>
                    {filterOptions.slice(4).map(option => (
                      <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={activeFilters.starRating === parseInt(option.id.charAt(0))}
                          onChange={() => handleFilterChange(option.id)}
                          className="text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {filteredReviews.map((review, idx) => (
            <div key={review.id} className="bg-white rounded-lg p-6 shadow-sm border">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 font-medium text-sm">
                      {(review.authorName || 'A')
                        .split(' ')
                        .filter(Boolean)
                        .slice(0, 2)
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{review.authorName || 'Anonymous'}</h3>
                    <p className="text-sm text-gray-500">
                      {review.updateTime ? new Date(review.updateTime).toLocaleDateString() : '—'}
                      {review.locationName ? (
                        <span className="ml-2 text-gray-400">• {review.locationName}</span>
                      ) : null}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="bg-[#000c34] hover:bg-[#000c34]/90 text-white text-sm px-3 py-1.5 rounded flex items-center space-x-1">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>Respond</span>
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
                    <PencilSimple className="w-4 h-4" weight="regular" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, starIdx) => (
                  <svg
                    key={starIdx}
                    className="w-4 h-4"
                    fill={starIdx < (review.rating || 0) ? '#FFC037' : '#E5E7EB'}
                    stroke={starIdx < (review.rating || 0) ? '#FFC037' : '#E5E7EB'}
                    strokeWidth={1}
                    viewBox="0 0 24 24"
                  >
                    <path d="M8.5 0L10.4084 5.87336L16.584 5.87336L11.5878 9.50329L13.4962 15.3766L8.5 11.7467L3.50383 15.3766L5.41219 9.50329L0.416019 5.87336L6.59163 5.87336L8.5 0Z" />
                  </svg>
                ))}
              </div>
              
              <p className="text-sm text-gray-700 leading-relaxed">{review.comment || '—'}</p>
            </div>
          ))}
        </div>

        {/* Summary Sidebar */}
        <div className="w-80 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="font-semibold text-gray-800 mb-4">Summary</h3>
            
            {/* Total Reviews */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-600 mb-2">Total Reviews</p>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-gray-800">{ratingSummary?.total || 0}</p>
                <span className="text-green-500 text-sm flex items-center gap-1">
                  <ArrowRight className="w-4 h-4" weight="regular" /> {hasLocationMappings ? 'Live' : 'N/A'}
                </span>
              </div>
            </div>

            {/* Average Rating */}
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">Average Rating</p>
              <div className="text-2xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                {typeof ratingSummary?.average === 'number' ? ratingSummary.average : 'N/A'}
                <svg
                  className="w-6 h-6"
                  fill="#FFC037"
                  stroke="#FFC037"
                  strokeWidth={1}
                  viewBox="0 0 24 24"
                >
                  <path d="M8.5 0L10.4084 5.87336L16.584 5.87336L11.5878 9.50329L13.4962 15.3766L8.5 11.7467L3.50383 15.3766L5.41219 9.50329L0.416019 5.87336L6.59163 5.87336L8.5 0Z" />
                </svg>
              </div>
              
              {/* Rating Breakdown */}
              <div className="space-y-2">
                {ratingSummary?.breakdown && Object.entries(ratingSummary.breakdown).reverse().map(([star, count]) => {
                  const getBarColor = (star) => {
                    switch (star) {
                      case '5': return 'bg-teal-400'; // teal/mint green
                      case '4': return 'bg-purple-500'; // purple
                      case '3': return 'bg-orange-400'; // orange/amber
                      case '2': return 'bg-gray-800'; // dark gray/black
                      case '1': return 'bg-red-500'; // red
                      default: return 'bg-blue-600';
                    }
                  };
                  
                  return (
                    <div key={star} className="flex items-center gap-3 text-sm text-gray-600">
                      <span className="w-4 text-center">{star}</span>
                      <div className="h-2 flex-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`${getBarColor(star)} h-full rounded-full`}
                          style={{ width: `${ratingSummary.total ? (count / ratingSummary.total) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <span className="w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <button 
            onClick={() => navigate('/dashboard/settings')}
            className="w-full text-sm text-purple-700 hover:text-purple-800 font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Manage connections & locations
          </button>
        </div>
      </div>
    </div>
  );
}
